import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Video, LayoutGrid, Sparkles, Clock, TrendingUp } from 'lucide-react';

const AIImageGeneratorInstagram = () => {
  const faqs = [
    {
      question: "What image sizes work best for Instagram?",
      answer: "Instagram supports multiple formats: 1:1 square (1080x1080px) for feed posts, 4:5 portrait (1080x1350px) for better engagement, and 9:16 vertical (1080x1920px) for Stories and Reels. Our AI generates all these formats automatically."
    },
    {
      question: "Can I create Instagram Reels thumbnails with AI?",
      answer: "Yes! Generate eye-catching 9:16 thumbnails for Reels that grab attention in the feed. Create scroll-stopping visuals that increase click-through rates."
    },
    {
      question: "Are AI images good for Instagram engagement?",
      answer: "Absolutely! AI-generated images often outperform stock photos because they're unique and customizable. Create on-brand visuals that stand out in crowded feeds."
    },
    {
      question: "Can I create a consistent Instagram aesthetic with AI?",
      answer: "Yes! Use similar prompts and styles across your images to maintain a cohesive feed aesthetic. Generate themed content batches for consistent branding."
    }
  ];

  const features = [
    {
      icon: <LayoutGrid className="w-6 h-6" />,
      title: "All Instagram Formats",
      description: "Generate 1:1 feed posts, 4:5 portraits, 9:16 Stories & Reels in one click"
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Reels Thumbnails",
      description: "Create scroll-stopping 9:16 thumbnails that boost Reels views and engagement"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Aesthetic Consistency",
      description: "Build a cohesive Instagram feed with matching styles across all posts"
    },
    {
      icon: <Image className="w-6 h-6" />,
      title: "Story Backgrounds",
      description: "Generate beautiful backgrounds for text overlays and Story templates"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Trending Visuals",
      description: "Create content that matches current Instagram trends and aesthetics"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Batch Content Creation",
      description: "Generate a month's worth of Instagram content in under an hour"
    }
  ];

  const useCases = [
    "Influencers creating daily feed content",
    "Brands maintaining Instagram presence",
    "Social media managers scheduling posts",
    "Small businesses showcasing products",
    "Coaches and consultants sharing tips",
    "Artists promoting their work",
    "Food bloggers creating styled content",
    "Travel accounts generating destination posts"
  ];

  const formatSpecs = [
    { format: "Feed Post (Square)", size: "1080x1080px", ratio: "1:1", use: "Standard feed posts" },
    { format: "Feed Post (Portrait)", size: "1080x1350px", ratio: "4:5", use: "Higher engagement posts" },
    { format: "Stories/Reels", size: "1080x1920px", ratio: "9:16", use: "Stories, Reels, vertical video" },
    { format: "Carousel", size: "1080x1080px", ratio: "1:1", use: "Multi-image posts" },
  ];

  return (
    <>
      <SEO
        title="AI Instagram Image Generator | Create Viral Feed, Stories & Reels Content"
        description="Generate Instagram-optimized images with AI. Perfect 1:1 feed posts, 4:5 portraits, 9:16 Stories and Reels. Create a month of content in minutes. Boost your engagement."
        keywords="instagram image generator ai, ai instagram posts, instagram content ai, instagram feed generator, story images ai, reels thumbnails, instagram content creator, instagram aesthetic generator"
        url="/ai-image-generator-instagram"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Instagram Image Generator', url: '/ai-image-generator-instagram' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 text-pink-500 border-pink-500/20">
                Instagram-Optimized AI
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Instagram Image Generator
                <span className="block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">Create Viral Content That Converts</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate scroll-stopping Instagram content with AI. Feed posts, Stories, Reels thumbnails 
                in perfect dimensions. Build your aesthetic and boost engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
                  <Link to="/try-generator">
                    Create Instagram Content <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-pink-500" />
                  <span>All Instagram Formats</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span>Generate in Seconds</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-pink-500" />
                  <span>Boost Engagement</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Instagram Format Specs */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">
              Perfect Dimensions for Every Instagram Format
            </h2>
            <p className="text-center text-muted-foreground mb-12">
              Our AI automatically generates the right sizes for each Instagram placement
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {formatSpecs.map((spec, index) => (
                <Card key={index} className="border-pink-500/10">
                  <CardContent className="p-4 text-center">
                    <h3 className="font-semibold mb-1">{spec.format}</h3>
                    <p className="text-lg font-bold text-pink-500">{spec.size}</p>
                    <p className="text-sm text-muted-foreground">{spec.ratio} ratio</p>
                    <p className="text-xs text-muted-foreground mt-2">{spec.use}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Creators Love Our Instagram Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-pink-500/10 hover:border-pink-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/10 to-purple-500/10 flex items-center justify-center text-pink-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every Instagram Creator
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-background/50">
                    <CheckCircle className="w-5 h-5 text-pink-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Transform Your Instagram Content Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join thousands of creators using AI to build stunning Instagram feeds. 
                Generate a month of content in minutes, not days.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
                <Link to="/try-generator">
                  Generate Instagram Content Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorInstagram;
